/** Shipment list / detail → Create Receipt eligibility and payloads. */

export const CREATE_RECEIPT_BLOCKED_SHIPMENT_STATUSES = ["Cancelled", "Closed"];

export const LOCKED_SHIPMENT_STATUSES = ["Closed", "Cancelled"];

export const FULLY_RECEIPTED_SHIPMENT_STATUS = "Full Receipt Created";

/**
 * @param {string|{ name?: string, id?: string }|undefined} status
 * @returns {string}
 */
export const shipmentStatusName = status => {
  if (!status) {
    return "";
  }
  if (typeof status === "string") {
    return status;
  }
  return status.name || status.id || "";
};

/**
 * @param {string|{ name?: string, id?: string }|undefined} status
 * @returns {boolean}
 */
export const isFullyReceiptedShipment = status =>
  shipmentStatusName(status) === FULLY_RECEIPTED_SHIPMENT_STATUS;

/**
 * @param {number|string} qty
 * @returns {number}
 */
export const toQtyNumber = qty => {
  const value = Number(qty);
  return Number.isFinite(value) ? value : 0;
};

/**
 * @param {Array<{ id: number, status: string, name?: string, reference?: string }>} shipments
 * @returns {{ ok: boolean, reason: string }}
 */
export const createReceiptListSelectionState = shipments => {
  if (!shipments?.length) {
    return { ok: false, reason: "Select at least one shipment." };
  }

  const blocked = shipments.filter(shipment =>
    CREATE_RECEIPT_BLOCKED_SHIPMENT_STATUSES.includes(shipmentStatusName(shipment.status)),
  );
  if (blocked.length) {
    const label = blocked[0].reference || blocked[0].name || `Shipment ${blocked[0].id}`;
    return {
      ok: false,
      reason: `Cannot create a receipt for ${label} (${shipmentStatusName(blocked[0].status)}).`,
    };
  }

  return { ok: true, reason: "" };
};

/**
 * @param {{ status?: string, name?: string, reference?: string }} orderData
 * @returns {{ ok: boolean, reason: string }}
 */
export const createReceiptDetailState = orderData => {
  if (!orderData?.id) {
    return { ok: false, reason: "Shipment is not loaded." };
  }

  const statusName = shipmentStatusName(orderData.status);
  if (CREATE_RECEIPT_BLOCKED_SHIPMENT_STATUSES.includes(statusName)) {
    const label = orderData.reference || orderData.name || "This shipment";
    return {
      ok: false,
      reason: `Cannot create a receipt for ${label} (${statusName}).`,
    };
  }

  return { ok: true, reason: "" };
};

/**
 * Whole units still open to receive on a consignment line.
 *
 * @param {{ dispatched_qty?: number|string, received_qty?: number|string }} line
 * @returns {number}
 */
export const lineOpenReceiptQty = line => {
  const dispatched = toQtyNumber(line.dispatched_qty);
  const received = toQtyNumber(line.received_qty);
  const open = Math.floor(dispatched - received);
  return open > 0 ? open : 0;
};

/**
 * @param {object} item Consignment item from detail API.
 * @returns {{
 *   line_id: number,
 *   order_number: string,
 *   product_number: string,
 *   product_name: string,
 *   dispatched_qty: number,
 *   received_qty: number,
 *   open_qty: number,
 * }}
 */
export const mapConsignmentItemToReceiptLine = item => ({
  line_id: item.id,
  order_number: item.line?.order_no ?? "",
  product_number: item.product_number ?? item.line?.product ?? "",
  product_name: item.description ?? "",
  dispatched_qty: toQtyNumber(item.dispatched_qty),
  received_qty: toQtyNumber(item.received_qty),
  open_qty: lineOpenReceiptQty(item),
});

/**
 * @param {Array<object>} items
 * @returns {{ receivable: Array<object>, skipped: Array<{ line_id: number, order_number: string, product_number: string, reason: string }> }}
 */
export const partitionConsignmentItemsForReceipt = items => {
  const receivable = [];
  const skipped = [];

  (items || []).forEach(item => {
    receivable.push(mapConsignmentItemToReceiptLine(item));
  });

  return { receivable, skipped };
};

/**
 * @param {Array<{ id: number, name?: string, reference?: string, items?: object[] }>} consignments
 * @returns {{ groups: Array<object>, skipped_lines: Array<object> }}
 */
export const buildReceiptPreviewFromConsignments = consignments => {
  const groups = [];
  const skipped_lines = [];

  (consignments || []).forEach(consignment => {
    const { receivable, skipped } = partitionConsignmentItemsForReceipt(consignment.items);
    skipped.forEach(line => skipped_lines.push(line));
    groups.push({
      consignment_id: consignment.id,
      shipment_name: consignment.name,
      reference: consignment.reference,
      fully_receipted: isFullyReceiptedShipment(consignment.status),
      lines: receivable,
    });
  });

  return { groups, skipped_lines };
};

/**
 * @param {Array<{ lines?: Array<{ line_id: number }> }>} groups
 * @param {{ preselectedLineIds?: number[], selectAllReceivable?: boolean }} options
 * @returns {Set<number>}
 */
export const initialCheckedReceiptLineIds = (groups, options = {}) => {
  const receivableLines = (groups || []).flatMap(group => group.lines || []);
  const allIds = receivableLines.map(line => line.line_id);
  const preselected = options.preselectedLineIds?.filter(id => allIds.includes(id)) ?? [];
  const defaultCheckedIds = receivableLines
    .filter(line => line.open_qty > 0)
    .map(line => line.line_id);

  if (preselected.length) {
    return new Set(preselected);
  }

  return new Set(defaultCheckedIds);
};

/**
 * @param {string|number|undefined} value
 * @returns {number|null}
 */
export const parseReceiveQtyInput = value => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return null;
  }
  const num = Number(trimmed);
  if (!Number.isFinite(num) || !Number.isInteger(num)) {
    return null;
  }
  return num;
};

/**
 * @param {Record<number, string>|undefined} lineQuantities
 * @param {number} lineId
 * @param {{ open_qty: number }} line
 * @returns {string}
 */
export const getEditedReceiveQtyText = (lineQuantities, lineId, line) => {
  if (lineQuantities && lineQuantities[lineId] !== undefined) {
    return String(lineQuantities[lineId]);
  }
  return String(line.open_qty);
};

/**
 * Same rules as the full-page create receipt form: positive whole units only.
 * Open qty is a default hint, not a cap (API does not enforce dispatched limits).
 *
 * @param {string|number|undefined} qtyInput
 * @returns {{ valid: boolean, qty?: number }}
 */
export const validateReceiveLineQty = qtyInput => {
  const parsed = parseReceiveQtyInput(qtyInput);
  if (parsed === null || parsed < 1) {
    return { valid: false };
  }
  return { valid: true, qty: parsed };
};

/**
 * @param {Record<number, string>|undefined} lineQuantities
 * @param {number} lineId
 * @param {{ open_qty: number }} line
 * @returns {number}
 */
export const effectiveReceiveQty = (lineQuantities, lineId, line) => {
  const check = validateReceiveLineQty(getEditedReceiveQtyText(lineQuantities, lineId, line));
  if (check.valid) {
    return check.qty;
  }
  return 0;
};

/**
 * @param {{
 *   checkedLineIds: Iterable<number>,
 *   lineQuantities?: Record<number, string>,
 *   linesById: Record<number, { open_qty: number }>,
 * }} params
 * @returns {{ receive_qty: Array<{ id: number, receive_qty: number }> }}
 */
export const buildConsignmentToReceiptPayload = ({ checkedLineIds, lineQuantities, linesById }) => {
  const receive_qty = [];

  for (const lineId of checkedLineIds) {
    const line = linesById[lineId];
    if (!line) {
      continue;
    }
    const check = validateReceiveLineQty(getEditedReceiveQtyText(lineQuantities, lineId, line));
    if (check.valid) {
      receive_qty.push({ id: lineId, receive_qty: check.qty });
    }
  }

  return { receive_qty };
};
